import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { User, SubscriptionTier } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AuthContext } from './auth-context';
import { fetchUserProfile, updateLastLogin, verifyAdminAccess } from './auth-utils';
import { loginWithEmail, registerWithEmail, logout as authLogout } from './auth-methods';
import { UserRole } from '@/types'; // Keep UserRole if needed for register

// Removed SubscriptionTier import as upgrade logic moved

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Removed isSynchronizing state
  const { toast } = useToast();

  // Centralized handler for session changes
  const handleSessionChange = useCallback(async (session: any | null) => {
    // console.log('Handling session change:', session ? `User ID: ${session.user.id}` : 'No session');
    if (session?.user) {
      setIsLoading(true);
      try {
        const userId = session.user.id;
        const userData = await fetchUserProfile(userId);
        if (userData) {
          setUser(userData);
          await updateLastLogin(userId);
          // console.log('User state updated from session change:', userData);
        } else {
          console.warn('No user profile found after session change for ID:', userId);
          setUser(null);
          // Optionally sign out if profile is missing unexpectedly
          // await supabase.auth.signOut();
        }
      } catch (error) {
        console.error('Error fetching profile during session change:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    } else {
      // No session or session ended
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  // Effect for initializing auth and listening to state changes
  useEffect(() => {
    setIsLoading(true);
    // console.log('AuthProvider Mounted: Setting up auth listener and getting initial session.');

    // Immediately check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // console.log('Initial session check:', session ? 'Session found' : 'No session');
      if (session) {
        handleSessionChange(session);
      } else {
        setIsLoading(false);
      }
    }).catch(error => {
        console.error("Error getting initial session:", error);
        setIsLoading(false);
    });

    // Subscribe to future auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // console.log('onAuthStateChange event:', _event);
      handleSessionChange(session);
    });

    // Cleanup subscription on unmount
    return () => {
      // console.log('AuthProvider Unmounted: Unsubscribing from auth changes.');
      subscription?.unsubscribe();
    };
  }, [handleSessionChange]);

  // --- Simplified Auth Actions --- 

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { error } = await loginWithEmail(email, password);
      if (error) {
        console.error('Login Provider Error:', error);
        toast({
          title: "Échec de connexion",
          description: error.message || "Veuillez vérifier vos identifiants et réessayer.",
          variant: "destructive",
        });
        setIsLoading(false);
        return { success: false, error };
      }
      // Success: onAuthStateChange will handle setting the user state and final loading state
      // console.log('Login call successful, waiting for auth state change...');
      // No need to return data here, success is enough
      return { success: true }; 
    } catch (error: any) {
      // Catch unexpected errors from the login method itself
      console.error('Login Provider Unexpected Error:', error);
      toast({ title: "Erreur", description: "Une erreur inattendue est survenue.", variant: "destructive" });
      setIsLoading(false);
      return { success: false, error };
    }
  }, [toast]);

  const register = useCallback(async (email: string, password: string, role: UserRole, metadata?: Record<string, any>) => {
    setIsLoading(true);
    try {
      const { data, error } = await registerWithEmail(email, password, role, metadata);
      if (error) {
         console.error('Register Provider Error:', error);
         toast({
           title: "Échec d'inscription",
           description: error.message || "Veuillez réessayer plus tard.",
           variant: "destructive",
         });
         setIsLoading(false);
         return { success: false, error };
      }

      // Registration successful, show confirmation toast.
      // The auth listener might pick up the user session if auto-confirmed, or user needs to confirm email.
      if (data?.user?.identities?.length === 0) { // Check if email confirmation is likely needed
           toast({
             title: "Inscription réussie",
             description: "Veuillez vérifier votre email pour confirmer votre compte.",
           });
      } else {
           toast({
             title: "Inscription réussie",
             description: "Compte créé avec succès! Vous pouvez maintenant vous connecter.",
           });
      }
      setIsLoading(false); // Set loading false after registration attempt
      return { success: true }; // Indicate success
    } catch (error: any) {
      console.error('Register Provider Unexpected Error:', error);
      toast({ title: "Erreur", description: "Une erreur inattendue est survenue.", variant: "destructive" });
      setIsLoading(false);
      return { success: false, error };
    }
  }, [toast]);

  const logout = useCallback(async () => {
    // console.log('Logout handler called');
    // No need for setIsLoading(true) here, as onAuthStateChange will handle it.
    try {
      const { error } = await authLogout();
      if (error) {
        console.error('Logout Provider Error:', error);
        toast({ title: "Erreur de déconnexion", description: error.message, variant: "destructive" });
        // Don't return error object? Or return { success: false }
      } else {
         // Success: onAuthStateChange handles setUser(null) and isLoading.
         // console.log('Logout call successful, waiting for auth state change...');
         toast({ title: "Déconnexion réussie" });
      }
    } catch (error) {
       console.error('Logout Provider Unexpected Error:', error);
       toast({ title: "Erreur", description: "Une erreur inattendue est survenue.", variant: "destructive" });
    }
  }, [toast]);
  
  // Function to check if the current user is an admin
  const isAdmin = useCallback((): boolean => {
      if (!user) return false;
      // Simple check if user role is admin
      return user.role === 'admin';
  }, [user]);

  // --- Removed Handlers ---
  // loginWithSeedPhraseHandler removed
  // registerWithSeedPhraseHandler removed
  // upgradeSubscriptionHandler removed (use service directly where needed)
  // synchronizeAccounts removed

  const value = useMemo(() => ({
    user,
    isLoading,
    login,
    register,
    logout,
    isAdmin,
    // Add missing properties with default/inactive values to satisfy AuthContextType
    isSynchronizing: false, 
    loginWithSeedPhrase: async (seedPhrase: string) => {
      console.warn('loginWithSeedPhrase is not implemented in AuthProvider');
      return { success: false, error: 'Not implemented' };
    },
    registerWithSeedPhrase: async (role: UserRole, metadata?: Record<string, any>) => {
      console.warn('registerWithSeedPhrase is not implemented in AuthProvider');
      return { success: false, error: 'Not implemented' };
    },
    upgradeSubscription: async (tier: SubscriptionTier) => {
      console.warn('upgradeSubscription logic should be handled elsewhere (e.g., subscription service)');
      return { success: false, error: 'Not implemented in AuthProvider' };
    },
    synchronizeAccounts: async () => {
      console.warn('synchronizeAccounts is not implemented in AuthProvider');
      return { success: false, error: 'Not implemented' };
    }
    // Removed upgradeSubscription, synchronizeAccounts, loginWithSeedPhrase, registerWithSeedPhrase
  }), [user, isLoading, login, register, logout, isAdmin]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
